/**
 * Phase 14.5 → Phase 17 — `/campaigns/:campaignId` detail dispatch.
 *
 * Phase 17 priority order: try unified first; if 404, probe legacy
 * pk / geometry / optimisation collections. Legacy hits land on
 * their original detail page (the one with the LEGACY banner —
 * see legacy detail components for that affordance).
 */
import { useEffect, useState } from "react";
import { Navigate, useParams, useLocation, Link } from "react-router-dom";
import { Compass } from "lucide-react";
import pkCampaigns from "@/api/pkCampaigns";
import geometryCampaigns from "@/api/geometryCampaigns";
import optimisationCampaigns from "@/api/optimisationCampaigns";
import unifiedCampaigns from "@/api/unifiedCampaigns";
import UnifiedCampaignDetailPage from "@/pages/UnifiedCampaignDetailPage";

const VALID_FAMILIES = new Set(["unified", "pk", "geometry", "optimisation"]);

export default function CampaignDetailDispatchPage() {
    const { campaignId } = useParams();
    const location = useLocation();
    const stateFamily = location?.state?.family;
    const initial = VALID_FAMILIES.has(stateFamily) ? stateFamily : null;
    const [resolved, setResolved] = useState(initial);

    useEffect(() => {
        if (initial) return undefined;
        if (!campaignId) {
            setResolved("not-found");
            return undefined;
        }
        let cancelled = false;
        (async () => {
            // Phase 17 — unified always wins.
            try {
                await unifiedCampaigns.get(campaignId);
                if (!cancelled) setResolved("unified");
                return;
            } catch (_) { /* fall through */ }
            const settled = await Promise.allSettled([
                pkCampaigns.get(campaignId),
                geometryCampaigns.get(campaignId),
                optimisationCampaigns.get(campaignId),
            ]);
            if (cancelled) return;
            const [pkOk, geomOk, optOk] = settled.map(
                (r) => r.status === "fulfilled" && r.value,
            );
            if (pkOk) setResolved("pk");
            else if (geomOk) setResolved("geometry");
            else if (optOk) setResolved("optimisation");
            else setResolved("not-found");
        })();
        return () => { cancelled = true; };
    }, [campaignId, initial]);

    if (resolved === null) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center text-sm text-ux-text-3"
                 data-testid="campaign-detail-dispatch-loading">
                Resolving campaign…
            </div>
        );
    }
    if (resolved === "unified") {
        return <UnifiedCampaignDetailPage />;
    }
    if (resolved === "pk") {
        return <Navigate to={`/pk-campaigns/${campaignId}`} replace
                         data-testid="campaign-detail-dispatch-target-pk" />;
    }
    if (resolved === "geometry") {
        return <Navigate to={`/geometry-campaigns/${campaignId}`} replace
                         data-testid="campaign-detail-dispatch-target-geometry" />;
    }
    if (resolved === "optimisation") {
        return <Navigate to={`/optimisation-campaigns/${campaignId}`} replace
                         data-testid="campaign-detail-dispatch-target-optimisation" />;
    }
    return (
        <div className="flex min-h-[40vh] items-center justify-center"
             data-testid="campaign-detail-dispatch-not-found">
            <div className="max-w-md rounded-md border border-ux-border-1 bg-ux-surface-1 p-6 text-center">
                <Compass className="mx-auto mb-3 size-6 text-ux-text-3" />
                <p className="text-sm font-medium text-ux-text-1">Campaign not found</p>
                <p className="mt-1 text-xs text-ux-text-3">
                    No unified, Pk, Geometry, or Optimisation campaign matches{" "}
                    <code className="rounded bg-ux-surface-2 px-1 py-0.5">{campaignId}</code>.
                </p>
                <Link to="/campaigns" className="mt-4 inline-block text-xs underline"
                      data-testid="campaign-detail-dispatch-back-link">
                    ← Back to all campaigns
                </Link>
            </div>
        </div>
    );
}
